/**
 * Geometry for the two chart types ECharts doesn't ship: council seats (hemicycle) and the ward tile map.
 * Both are drawn as ECharts scatter / heatmap series from these coordinates.
 */

export interface Seat { x: number; y: number; angle: number }

/** Seats per row, inner → outer. 7 + 9 + 10 + 11 + 13 = 50 (the gallery hemicycle). */
export const COUNCIL_ROWS = [7, 9, 10, 11, 13];

/**
 * Hemicycle seat centers in a unit box: x ∈ [-1, 1], y ∈ [0, 1] (0 = baseline).
 * Sorted left → right by angle, so filling the first N seats with "likely yes" reads as a left-to-right sweep.
 */
export function seatLayout(rows: number[] = COUNCIL_ROWS, inner = 0.37): Seat[] {
  const seats: Seat[] = [];
  rows.forEach((n, r) => {
    const radius = inner + ((1 - inner) * r) / Math.max(1, rows.length - 1);
    for (let i = 0; i < n; i++) {
      const angle = Math.PI * (1 - i / Math.max(1, n - 1));
      seats.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle), angle });
    }
  });
  return seats.sort((a, b) => b.angle - a.angle || a.y - b.y);
}

/** Members ordered yes → toss-up → no, matched to seats left → right. */
export function assignSeats<M extends { status: "yes" | "tossup" | "no" }>(members: M[], seats = seatLayout()) {
  const order = { yes: 0, tossup: 1, no: 2 } as const;
  const sorted = members.map((m, i) => ({ m, i })).sort((a, b) => order[a.m.status] - order[b.m.status] || a.i - b.i).map((x) => x.m);
  return sorted.slice(0, seats.length).map((m, i) => ({ ...seats[i], member: m }));
}

/**
 * Stylized tile map: one square per ward, north at top. Positions are APPROXIMATE (the UI says so).
 * [col offset, wards left → right] per row. Replace with a GIS-derived layout if you have one; keep 50 unique wards.
 */
export const WARD_TILE_ROWS: [number, number[]][] = [
  [2, [41, 50, 49]],
  [1, [45, 39, 40, 48]],
  [1, [38, 30, 33, 47, 46]],
  [0, [36, 31, 35, 32, 44, 43]],
  [0, [29, 37, 26, 1, 2, 42]],
  [0, [24, 28, 27, 25, 34, 4]],
  [0, [22, 12, 11, 3, 20, 5]],
  [0, [23, 14, 15, 16, 6, 7]],
  [0, [13, 18, 17, 21, 8, 10]],
  [1, [19, 9]],
];

export const WARD_TILES: { ward: number; col: number; row: number }[] = WARD_TILE_ROWS.flatMap(([off, wards], row) =>
  wards.map((ward, i) => ({ ward, col: off + i, row })));

export const WARD_GRID = { cols: Math.max(...WARD_TILES.map((t) => t.col)) + 1, rows: WARD_TILE_ROWS.length };
